import { ListChecks, Search, Plus, ClipboardList, CheckCircle2, Package } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { ChecklistStats } from '../types';

interface ChecklistsHeroProps {
  stats: ChecklistStats;
  globalFilter: string;
  onGlobalFilterChange: (value: string) => void;
  onCreateClick: () => void;
}

function StatCard({ value, label, icon: Icon }: { value: string | number; label: string; icon: React.ComponentType<{ className?: string }> }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
      <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <div className="text-xl font-bold text-white">{value}</div>
        <div className="text-xs text-primary-100">{label}</div>
      </div>
    </div>
  );
}

export function ChecklistsHero({
  stats,
  globalFilter,
  onGlobalFilterChange,
  onCreateClick,
}: ChecklistsHeroProps) {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-[35vh] overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
      {/* Decorative blur circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl opacity-10" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-accent rounded-full blur-3xl opacity-15" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium text-white mb-6 border border-white/20 animate-fade-in">
          <ListChecks className="w-4 h-4" />
          {t('checklists.badge', 'Project Checklists')}
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white animate-slide-up">
          {t('checklists.heroTitle', 'Track your')}{' '}
          <span className="text-accent">{t('checklists.heroTitleHighlight', 'projects')}</span>
        </h1>

        {/* Subtitle */}
        <p className="mt-4 text-lg text-primary-100 max-w-2xl animate-slide-up animation-delay-100">
          {t('checklists.heroSubtitle', 'Organize your IKEA shopping, import from PDF, track purchases and installation progress.')}
        </p>

        {/* Search bar and Create button */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 max-w-2xl animate-slide-up animation-delay-200">
          <div className="relative flex-1">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="search"
              value={globalFilter}
              onChange={e => onGlobalFilterChange(e.target.value)}
              placeholder={t('checklists.searchAllPlaceholder', 'Search across all checklists...')}
              className="w-full pl-14 pr-6 py-4 text-lg rounded-2xl bg-white/95 backdrop-blur-sm shadow-xl focus:ring-4 focus:ring-accent/30 focus:outline-none border-0 text-gray-900 placeholder-gray-500"
            />
          </div>
          <button
            onClick={onCreateClick}
            className="flex items-center justify-center gap-2 px-6 py-4 text-base font-medium text-primary-700 bg-white rounded-2xl shadow-xl hover:bg-gray-50 transition-colors"
          >
            <Plus className="w-5 h-5" />
            {t('checklists.newChecklist', 'New Checklist')}
          </button>
        </div>

        {/* Stats row */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl animate-fade-in animation-delay-300">
          <StatCard
            value={stats.totalChecklists}
            label={t('checklists.stats.checklists', 'Checklists')}
            icon={ClipboardList}
          />
          <StatCard
            value={stats.totalItems}
            label={t('checklists.stats.items', 'Total Items')}
            icon={Package}
          />
          <StatCard
            value={stats.completedItems}
            label={t('checklists.stats.completed', 'Completed')}
            icon={CheckCircle2}
          />
          <StatCard
            value={stats.estimatedTotal > 0 ? `$${stats.estimatedTotal.toLocaleString()}` : '-'}
            label={t('checklists.stats.estimated', 'Est. Total')}
            icon={ListChecks}
          />
        </div>
      </div>

      {/* Wave divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 80" fill="none" className="w-full h-auto">
          <path
            d="M0 80L60 70C120 60 240 40 360 35C480 30 600 40 720 45C840 50 960 50 1080 45C1200 40 1320 30 1380 25L1440 20V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
}
